import {combine, restore} from 'effector';

import {
    fetchCodeForServiceOrProducts,
    fetchDocumentInvoiceCreate,
    fetchDocumentInvoiceXmlValidation,
    fetchInvoiceIrn,
} from 'api/documents/create';

import {rootDomain} from 'core/rootDomain';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {
    AllowanceCharge,
    InfoValues,
    InvocieLineHolder,
    InvoiceLine,
    InvoiceLineChargeData,
    InvoiceLineErrorEntry,
    PageValues,
    RoundAndPrepayd,
    TaxesAndTotal,
} from 'types/invoices';

import {
    DEFAULT_LINE_VALUES,
    documnetPageValues,
    generalInfoPageValues,
    taxesPageValue,
} from './defaultVariables';

const domain = rootDomain.createDomain('InvoiceCreating');

export const $invoiceCreationPage = domain.createStore<number>(0);
export const $invoiceLineValues = domain.createStore<InvoiceLine[]>([]);
export const $invoiceLinesHolder = domain.createStore<InvocieLineHolder[]>([]);

const REQUIRED_FIELDS = ['qty', 'price', 'lines', 'vatCategory', 'vat', 'lineAmountWVat'] as const;
const REQUIRED_FIELDS_FOR_UNMARKED_ITEM = ['description', ...REQUIRED_FIELDS] as const;

function getInvalidFields(line: InvocieLineHolder): string[] {
    return REQUIRED_FIELDS.filter((field) => {
        if (field === 'vatCategory') {
            return !line.vatCategory?.value || line.vatCategory.value === '';
        }
        if (field === 'vat' && line.vatCategory?.value === 'S' && line.vat === '0') {
            return true;
        }
        const value = line[field as keyof InvocieLineHolder];
        return value === undefined || value === null || value === '';
    });
}

function getInvalidFieldsForUnmarkedItem(line: InvocieLineHolder): string[] {
    return REQUIRED_FIELDS_FOR_UNMARKED_ITEM.filter((field) => {
        if (field === 'vatCategory') {
            return !line.vatCategory?.value || line.vatCategory.value === '';
        }
        if (field === 'vat' && line.vatCategory?.value === 'S' && line.vat === '0') {
            return true;
        }
        const value = line[field as keyof InvocieLineHolder];
        return value === undefined || value === null || value === '';
    });
}

function collectLineErrors(lines: InvocieLineHolder[], basePath = ''): InvoiceLineErrorEntry[] {
    const result: InvoiceLineErrorEntry[] = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const path = basePath ? `${basePath}.${i}` : String(i);
        if (!line.isMarkedItem) {
            const fields = getInvalidFieldsForUnmarkedItem(line);

            if (fields.length > 0) result.push({index: path, fields});
        }
        if (line.packagingType && CUSTOMER_PACKAGE_TYPES.includes(line.packagingType)) {
            const fields = getInvalidFields(line);
            if (fields.length > 0) result.push({index: path, fields});
        }
        if (line.subRows?.length) {
            result.push(...collectLineErrors(line.subRows, path));
        }
    }
    return result;
}

export const $invoiceLinesErrors = $invoiceLinesHolder.map((lines) => collectLineErrors(lines));

function countUnitCodes(lines: InvocieLineHolder[]): string[] {
    const result: string[] = [];
    for (const line of lines) {
        if (line.packagingType && CUSTOMER_PACKAGE_TYPES.includes(line.packagingType)) {
            const codesToAdd = line.combinedCodes?.length ? line.combinedCodes : [line.code];
            result.push(...codesToAdd);
        }
        if (line.packagingType && TRANSPORT_PACKAGE_TYPES.includes(line.packagingType)) {
            result.push(line.code);
        }
        if (line.subRows?.length) {
            result.push(...countUnitCodes(line.subRows));
        }
    }
    return result;
}

export const $invoiceLinesHolderCodesTotal = $invoiceLinesHolder.map((lines) =>
    countUnitCodes(lines),
);

export const $invoiceLineChargeData = domain.createStore<InvoiceLineChargeData | null>(null);
export const $pageValues = domain.createStore<PageValues>(documnetPageValues);
export const $infoValues = domain.createStore<InfoValues>(generalInfoPageValues);

export const $taxesAndTotal = domain.createStore<{
    data: TaxesAndTotal;
    allowToSend: boolean;
}>({
    data: taxesPageValue,
    allowToSend: false,
});

export const setInitalLineValuesHolder = domain.createEvent<Partial<InvoiceLine> | null>();
export const $initalLineValuesHolder = domain.createStore<Partial<InvoiceLine> | null>(
    DEFAULT_LINE_VALUES,
);

export const invoiceCreationPage = domain.createEvent<number>();
export const goNextPage = domain.createEvent();
export const goPrevPage = domain.createEvent();
export const guarderFetchInvoice = domain.createEvent();
export const updatePageValues = domain.createEvent<PageValues>();
export const updateInfoValues = domain.createEvent<InfoValues>();
export const updateInfoValuesCityAndZip = domain.createEvent<{
    customerZip: string;
    customerCity: string;
    customerCountry: string;
    customerStreet: string;
}>();
export const updateInfoValuesPaymentMean = domain.createEvent<boolean>();

export const updateCustomerCompanyNameOrTIN = domain.createEvent<{value: string; label: string}>();
export const updateIRN = domain.createEvent<string>();
export const setInvoiceLinesHolder = domain.createEvent<InvocieLineHolder[]>();
export const updateInvoiceLinesHolder = domain.createEvent<{
    index?: number;
    depth: {step: number; id: string};
    data:
        | {columnId: string; value: string | string[] | null}
        | {columnId: string; value: string | string[] | null}[];
}>();
export const removeInvoiceLinesHolder = domain.createEvent<string>();
export const updateAllDataFromInvoiceLinesHolder = domain.createEvent<InvocieLineHolder[]>();
export const copyInvoiceLineData = domain.createEvent<{
    index: string;
    initialLineData?: Partial<InvocieLineHolder>;
}>();

export const addInvoiceLineValues = domain.createEvent<InvoiceLine[]>();

export const setInvoiceLineChargeData = domain.createEvent<InvoiceLineChargeData | null>();

export const removeInvoiceLineValues = domain.createEvent<number>();
export const updateTaxesAndTotal = domain.createEvent<{
    data: Partial<TaxesAndTotal>;
    allowToSend: boolean;
}>();
export const setTaxesAndTotalFromXml = domain.createEvent<{
    data: TaxesAndTotal;
    allowToSend: boolean;
}>();
export const updateTaxesAndTotalFromInvoiceLines = domain.createEvent<{
    data: TaxesAndTotal;
    allowToSend: boolean;
}>();
export const updateTaxesAndTotalRoundAndPrepayd = domain.createEvent<RoundAndPrepayd>();
export const updateTaxesAndTotalAllowanceCharge = domain.createEvent<AllowanceCharge>();
export const removeTaxesAndTotalAllowanceCharge = domain.createEvent<number>();

export const $validationErrorToPassNext = domain.createStore<boolean>(false);
export const handleValidationErrorToPassNext = domain.createEvent<boolean>();

export const fetchXmlValidationFx = domain.createEffect({
    name: 'fetchXmlValidationFx',
    handler: fetchDocumentInvoiceXmlValidation,
});

export const fetchInvocieXmlFx = domain.createEffect({
    name: 'fetchInvocieXmlFx',
    handler: fetchDocumentInvoiceCreate,
});

export const fetchInvoiceIrnFx = domain.createEffect({
    name: 'fetchInvoiceIrnFx',
    handler: fetchInvoiceIrn,
});

export const fetchCodeForServiceOrProductsFx = domain.createEffect({
    name: 'fetchCodeForServiceOrProductsFx',
    handler: fetchCodeForServiceOrProducts,
});

export const arrStores = combine([
    $infoValues,
    $pageValues,
    $invoiceLineChargeData,
    $invoiceLineValues,
    $taxesAndTotal,
    $invoiceLinesHolder,
    $invoiceLinesErrors,
]);

export const setShowGroupLinesByGtinModal = domain.createEvent<boolean>();
export const $showGroupLinesByGtinModal = restore(setShowGroupLinesByGtinModal, false);
export const setForceAllowToSend = domain.createEvent<boolean>();
export const $forceAllowToSend = restore(setForceAllowToSend, false);

export const $blockNavigationFromCreate = combine({$taxesAndTotal, $forceAllowToSend}, (params) => {
    const {$taxesAndTotal: taxesAndTotal, $forceAllowToSend: forceAllowToSend} = params;
    return !taxesAndTotal.allowToSend && !forceAllowToSend;
});
